import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProject, useProjects } from '../../hooks/queries/useProjects';
import { useProjectUIStore } from '../../store/projectUIStore';
import { TopicList } from './TopicList';
import { CollapsedTopicList } from './CollapsedTopicList';
import { ChatTopic } from './chat/ChatTopic';
import { NotesTopic } from './notes/NotesTopic';
import { NotesTopicErrorBoundary } from './notes/NotesTopicErrorBoundary';
import { ListTopic } from './list/ListTopic';
import { TaskTopic } from './tasks/TaskTopic';
import { MoodboardTopic } from './moodboard/MoodboardTopic';
import { BookmarksTopic } from './bookmarks/BookmarksTopic';
import { TableTopic } from './table/TableTopic';
import { TableTopicErrorBoundary } from './table/TableTopicErrorBoundary';
import { WelcomeScreen } from '../projects/welcome/WelcomeScreen';
import { NewTopicModal } from './NewTopicModal';
import { EmptyState } from './EmptyState';
import { useUser } from '../../hooks/queries/useAuth';
import { useViewport } from '../../contexts/ViewportContext';
import { useTopicList } from '../../contexts/TopicListContext';
import { logger } from '../../utils/logger';
import { useTopicSkeletonState } from '../../hooks/useTopicSkeletonState';
import { useTopicUIStore } from '../../store/topicUIStore';
import { useTopic } from '../../hooks/queries/useTopics';
import { useTopicFetching } from '../../hooks/useTopicFetching';

interface TopicContainerProps {
  onTopicSelect: (topicId: string) => void;
}

export function TopicContainer({ onTopicSelect }: TopicContainerProps) {
  const navigate = useNavigate();
  const { activeProjectId } = useProjectUIStore();
  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { data: activeProject } = useProject(activeProjectId);
  const { activeTopicId, setActiveTopicId, setInitialLoadComplete } = useTopicUIStore();
  const [isNewTopicModalOpen, setIsNewTopicModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Active project is now fetched directly with the useProject hook
  const { data: user } = useUser();
  const { isMobile } = useViewport();

  // Track key state changes for debugging
  const stateRef = useRef({
    lastProjectId: null as string | null,
    lastTopicId: null as string | null,
    projectChangeCount: 0,
    autoNavigationAttemptCount: 0,
  });

  // Track the current location
  const location = useLocation();

  // Use enhanced topic fetching with integrated auto-selection
  const {
    topics,
    loading: topicsLoading,
    fetching: topicsFetching,
    isTransitioning,
  } = useTopicFetching(activeProjectId, topicId =>
    navigate(`/project/${activeProjectId}/topic/${topicId}`)
  );

  // Log when active project changes
  useEffect(() => {
    if (activeProjectId !== stateRef.current.lastProjectId) {
      stateRef.current.projectChangeCount++;

      logger.component('🔄 TopicContainer: Project changed', {
        from: stateRef.current.lastProjectId,
        to: activeProjectId,
        projectChangeCount: stateRef.current.projectChangeCount,
        activeTopicId,
        hasTopics: topics.length > 0,
        pathname: location.pathname,
      });

      stateRef.current.lastProjectId = activeProjectId;
    }
  }, [activeProjectId, activeTopicId, topics, location.pathname]);

  // Fetch active topic details separately
  const { data: activeTopic, isLoading: activeTopicLoading } = useTopic(activeTopicId);

  // Use the unified skeleton state hook
  const { loading: skeletonLoading } = useTopicSkeletonState(activeProjectId);

  // Determine if the project has collaborators based on isShared flag
  const hasCollaborators = activeProject?.isShared || false;

  // We no longer need the loading screen manager here
  // Individual topic components will handle calling markRouteComplete

  // Only track initial load complete, but don't mark the route complete
  // That should be done by the individual topic components
  useEffect(() => {
    if (activeTopic && !topicsLoading && !activeTopicLoading) {
      // Mark initial load as complete for the TopicContainer
      // BUT let the individual topic components handle markRouteComplete
      setInitialLoadComplete(true);
    }
  }, [activeTopic, topicsLoading, activeTopicLoading, setInitialLoadComplete]);

  // Use TopicListContext instead of local state
  const {
    isCollapsed: isTopicListCollapsed,
    expandTopicList,
    toggleCollapse: toggleTopicList,
  } = useTopicList();

  const handleTopicListExpand = () => {
    expandTopicList();
  };

  // Listen for the toggle-sidebars event to handle keyboard shortcut
  React.useEffect(() => {
    const handleToggleSidebars = () => {
      toggleTopicList();
    };

    document.addEventListener('toggle-sidebars', handleToggleSidebars);

    return () => {
      document.removeEventListener('toggle-sidebars', handleToggleSidebars);
    };
  }, [toggleTopicList]);

  // Only display the welcome screen if we explicitly have no active project
  if (!activeProject) {
    return (
      <div className="h-full overflow-hidden">
        <WelcomeScreen
          hasProjects={projects.length > 0}
          latestProject={projects[0]}
          onProjectSelect={id => navigate(`/project/${id}`)}
          user={user}
          navigate={navigate}
          loading={projectsLoading}
        />
      </div>
    );
  }

  // Show loading state while waiting for initial topic load to complete
  // Check if we need to show a loading state
  if (topicsLoading || skeletonLoading || isTransitioning) {
    return <div className="h-full flex items-center justify-center" />;
  }

  // On mobile, show topics list when no topic is selected
  // Using the isMobile from ViewportContext to ensure consistency
  if (isMobile && !activeTopic) {
    return (
      <div className="h-full max-h-screen overflow-hidden">
        <TopicList
          onTopicSelect={onTopicSelect}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          onNewTopic={() => setIsNewTopicModalOpen(true)}
          forceLoading={skeletonLoading}
          projectId={activeProject.id}
        />

        <NewTopicModal
          isOpen={isNewTopicModalOpen}
          onClose={() => {
            setIsNewTopicModalOpen(false);
          }}
          projectId={activeProject.id}
          onTopicSelect={onTopicSelect}
          isFirstTopic={topics.length === 0}
        />
      </div>
    );
  }

  // Check if the project has topics
  const hasTopics = topics.length > 0;

  return (
    <div className="flex h-full overflow-hidden relative bg-[var(--content-bg-color)]">
      {/* Topics sidebar - only visible on desktop when there are topics */}
      {!isMobile && hasTopics && (
        <div
          className={`topics-sidebar hidden lg:block ${isTopicListCollapsed ? 'w-[56px]' : 'w-[280px]'} pt-2 pb-2 pl-2 transition-all duration-300 relative z-20`}
        >
          <div
            className={`h-full rounded-[var(--border-radius-lg)] ${isTopicListCollapsed ? 'overflow-hidden' : ''}`}
          >
            {/* Keep both components mounted but toggle display with CSS */}
            <div className={isTopicListCollapsed ? 'block' : 'hidden'}>
              <CollapsedTopicList
                projectId={activeProject.id}
                onTopicSelect={onTopicSelect}
                onExpand={handleTopicListExpand}
                onNewTopic={() => setIsNewTopicModalOpen(true)}
              />
            </div>
            <div className={isTopicListCollapsed ? 'hidden' : 'block'}>
              <TopicList
                onTopicSelect={onTopicSelect}
                onNewTopic={() => setIsNewTopicModalOpen(true)}
                projectId={activeProject.id}
              />
            </div>
          </div>
        </div>
      )}

      {/* Main content area */}
      <div
        className="flex-1 flex flex-col overflow-hidden relative z-5 bg-[var(--content-bg-color)]"
        style={{
          // When no topics exist, only account for the project sidebar width
          // Otherwise account for both sidebar and topic list width (total)
          // On desktop, also subtract the right margin (10px)
          // Special case for table topic: use less right margin (10px instead of 20px)
          maxWidth: isMobile
            ? '100vw'
            : hasTopics
              ? activeTopic?.topicType === 'table'
                ? 'calc(100vw - var(--sidebar-total-width) - 10px)' // Special case for tables: only 10px margin
                : 'calc(100vw - var(--sidebar-total-width) - 20px)' // 20px = 10px left + 10px right margin
              : 'calc(100vw - var(--sidebar-width) - 10px)',
          width: isMobile
            ? '100vw'
            : hasTopics
              ? activeTopic?.topicType === 'table'
                ? 'calc(100vw - var(--sidebar-total-width) - 10px)' // Special case for tables: only 10px margin
                : 'calc(100vw - var(--sidebar-total-width) - 20px)'
              : 'calc(100vw - var(--sidebar-width) - 20px)',
        }}
      >
        {activeTopic ? (
          <div className="flex-1 overflow-y-auto bg-[var(--content-bg-color)]">
            {activeTopic.topicType === 'ai_chat' && (
              <ChatTopic
                key={`chat-topic-${activeTopic.id}`}
                topic={activeTopic}
                project={activeProject}
                hasCollaborators={hasCollaborators}
                onBackClick={() => {
                  // First clear active topic, then navigate
                  setActiveTopicId(null);
                  navigate(`/project/${activeProject.id}`);
                }}
              />
            )}
            {activeTopic.topicType === 'notes' && (
              <NotesTopicErrorBoundary
                key={`notes-topic-error-boundary-${activeTopic.id}`}
                onReset={() => {
                  // Force a re-render by clearing and re-setting the topic
                  const currentTopicId = activeTopicId;
                  setActiveTopicId(null);
                  setTimeout(() => setActiveTopicId(currentTopicId), 0);
                }}
              >
                <NotesTopic
                  key={`notes-topic-${activeTopic.id}`}
                  topic={activeTopic}
                  hasCollaborators={hasCollaborators}
                  onBackClick={() => {
                    // First clear active topic, then navigate
                    setActiveTopicId(null);
                    navigate(`/project/${activeProject.id}`);
                  }}
                />
              </NotesTopicErrorBoundary>
            )}
            {activeTopic.topicType === 'list' && (
              <ListTopic
                key={`list-topic-${activeTopic.id}`}
                topic={activeTopic}
                hasCollaborators={hasCollaborators}
                onBackClick={() => {
                  // First clear active topic, then navigate
                  setActiveTopicId(null);
                  navigate(`/project/${activeProject.id}`);
                }}
              />
            )}
            {activeTopic.topicType === 'todo' && (
              <TaskTopic
                key={`task-topic-${activeTopic.id}`}
                topic={activeTopic}
                hasCollaborators={hasCollaborators}
                onBackClick={() => {
                  // First clear active topic, then navigate
                  setActiveTopicId(null);
                  navigate(`/project/${activeProject.id}`);
                }}
              />
            )}
            {activeTopic.topicType === 'moodboard' && (
              <MoodboardTopic
                key={`moodboard-topic-${activeTopic.id}`}
                topic={activeTopic}
                hasCollaborators={hasCollaborators}
                onBackClick={() => {
                  // First clear active topic, then navigate
                  setActiveTopicId(null);
                  navigate(`/project/${activeProject.id}`);
                }}
              />
            )}
            {activeTopic.topicType === 'bookmarks' && (
              <BookmarksTopic
                key={`bookmarks-topic-${activeTopic.id}`}
                topicId={activeTopic.id}
                topic={activeTopic}
                hasCollaborators={hasCollaborators}
                onBackClick={() => {
                  // First clear active topic, then navigate
                  setActiveTopicId(null);
                  navigate(`/project/${activeProject.id}`);
                }}
              />
            )}
            {activeTopic.topicType === 'table' && (
              <TableTopicErrorBoundary
                key={`table-topic-error-boundary-${activeTopic.id}`}
                onReset={() => {
                  // Force a re-render by clearing and re-setting the topic
                  const currentTopicId = activeTopicId;
                  setActiveTopicId(null);
                  setTimeout(() => setActiveTopicId(currentTopicId), 0);
                }}
              >
                <TableTopic
                  key={`table-topic-${activeTopic.id}`}
                  topic={activeTopic}
                  hasCollaborators={hasCollaborators}
                  onBackClick={() => {
                    // First clear active topic, then navigate
                    setActiveTopicId(null);
                    navigate(`/project/${activeProject.id}`);
                  }}
                />
              </TableTopicErrorBoundary>
            )}
          </div>
        ) : (
          // When no topic is active on desktop and no topics exist, show EmptyState
          <>
            {!isMobile && !hasTopics && !topicsLoading && !topicsFetching && !isTransitioning ? (
              // Empty project state on desktop - only shown when we're sure data is fully loaded
              <div className="flex items-center justify-center h-full w-full py-20 gradient-background">
                <EmptyState
                  onCreateTopic={() => setIsNewTopicModalOpen(true)}
                  hasTopics={hasTopics}
                />
              </div>
            ) : (
              // When topics exist but none selected, just show blank
              // This avoids flashing content during auto-selection
              <div className="h-full"></div>
            )}
          </>
        )}
      </div>

      <NewTopicModal
        isOpen={isNewTopicModalOpen}
        onClose={() => {
          setIsNewTopicModalOpen(false);
        }}
        projectId={activeProject.id}
        onTopicSelect={onTopicSelect}
        isFirstTopic={topics.length === 0}
      />
    </div>
  );
}
